import Vue from 'vue';
import axios from 'axios';
import { HTTP } from '../../../assets/js/axiosConfig';
import { firestoreDB, firebase } from '../../../assets/js/firebase';
import { CRUDGetAll } from '../../../assets/js/CRUDFirestore';

let source;

const state = {
  cruds: {
    areas: readJSON('areas'),
    brands: readJSON('brands'),
    categories: readJSON('categories'),
    conglomerates: readJSON('conglomerates'),
    collections: readJSON('collections'),
    distribution_channels: readJSON('distribution_channels'),
    ingredients: readJSON('ingredients'),
    package_capacities: readJSON('package_capacities'),
    patents: readJSON('patents'),
    roles: readJSON('roles'),
    segmentations: readJSON('segmentations'),
    targets: readJSON('targets'),
    textures: readJSON('textures'),
    texturotheques: readJSON('texturotheques'),
    themes: readJSON('themes'),
    types: readJSON('types'),
    type_packs: readJSON('type_packs'),
    type_skins: readJSON('type_skins'),
    verbatim_consos: readJSON('verbatim_consos'),
    additional_functions: readJSON('additional_functions'),
    mode_actions: readJSON('mode_actions'),
  },
  cmsResources: {
    related_inci: [],
  },
  crudMode: null, // Can be crud data index, 'create' , null
  newCRUD: null,
  updatedResource: '',
  deletedResource: {},
  generalSegmentations: [],
  segmentationProductsToLink: [],
  productLinkModal: false,
};

function sortDataAlpha(data) {
  return data.sort((a, b) => {
    const nameA = a.name.toLowerCase().trim();
    const nameB = b.name.toLowerCase().trim();
    if (nameA < nameB) {
      return -1;
    } if (nameA > nameB) {
      return 1;
    }
    // names must be equal
    return 0;
  });
}

const mutations = {
  GET_CRUDS(state, resourceName) {
    // Get all brands to populate choices
    if (state.cruds[resourceName]) {
      HTTP.get(`api/${resourceName}`)
        .then((response) => {
          state.updatedResource = resourceName;
          state.cruds[resourceName] = sortDataAlpha(response.data);
          if (resourceName === 'themes') {
            setThemeRecords(state.cruds[resourceName]);
            storeJSON(state.cruds[resourceName], resourceName);
          } else {
            storeJSON(state.cruds[resourceName], resourceName);
          }
        })
        .catch((err) => console.log(err));
    }
  },
  TOGGLE_CRUD_MODE(state, mode) {
    state.crudMode = mode;
  },
  SET_NEW_CRUD(state, data) {
    if (data) {
      state.newCRUD = {
        originator: data.name,
        item: data.item,
      };
    } else {
      state.newCRUD = null;
    }
  },
  SET_DELETED_RESOURCE(state, data) {
    state.deletedResource = data;
  },
  GENERAL_SEGMENTATION_FIRESTORE_LISTENER(state) {
    firestoreDB
      .collection(`${process.env.ENV_SHORTNAME}_generalSegmentations`)
      .onSnapshot((snapshot) => {
        state.generalSegmentations = [];
        let data;
        snapshot.docs.forEach((doc) => {
          data = doc.data();
          state.generalSegmentations.push({
            id: doc.id,
            general: data.generalSegmentation,
            themeId: data.themeId,
          });
        });
      });
  },
  SET_SEGMENTATION_PRODUCTS_TO_LINK(state, products) {
    state.segmentationProductsToLink = products;
  },
  TOGGLE_PRODUCT_LINK_MODAL(state) {
    state.productLinkModal = !state.productLinkModal;
  },
  async GET_FIRESTORE_RECORDS(state, resourceName) {
    if (state.cmsResources[resourceName]) {
      try {
        const datas = await CRUDGetAll(resourceName);

        state.cmsResources.related_inci = sortDataAlpha(datas);
      } catch (error) {
        console.error(error);
      }
    }
  },
};

const actions = {
  getCRUDs({ commit }, resourceName) {
    commit('GET_CRUDS', resourceName);
  },
  toggleCRUDMode({ commit }, mode) {
    commit('TOGGLE_CRUD_MODE', mode);
  },
  setNewCRUD({ commit }, data) {
    commit('SET_NEW_CRUD', data);
  },
  setDeletedResource({ commit }, data) {
    commit('SET_DELETED_RESOURCE', data);
  },
  generalSegmentationsFirebaseListener({ commit }) {
    commit('GENERAL_SEGMENTATION_FIRESTORE_LISTENER');
  },
  setSegmentationProductsToLink({ commit }, products) {
    commit('SET_SEGMENTATION_PRODUCTS_TO_LINK', products);
  },
  toggleProductLinkModal({ commit }, data) {
    commit('TOGGLE_PRODUCT_LINK_MODAL', data);
  },
  getFirestoreRecords({ commit }, resourceName) {
    commit('GET_FIRESTORE_RECORDS', resourceName);
  },
};

const getters = {
  getName: (state) => (id, idAlias, resource) => {
    const item = state.cruds[resource].find((item) => item[idAlias] === id);
    return item ? item.name : '';
  },
  getIdByName: (state) => (name, idAlias, resource) => {
    const item = state.cruds[resource].find(
      (item) => item.name.toLowerCase() === name.toLowerCase(),
    );
    return item ? item[idAlias] : '';
  },
  getConglomerateName: (state) => (brandName) => {
    let conglomerate = '';
    state.cruds.brands.filter((brand) => {
      if (
        brand.name.toLowerCase() === brandName.toLowerCase()
        && brand.hasOwnProperty('conglomerate_id')
      ) {
        conglomerate = state.cruds.conglomerates.find(
          (conglomerate) => conglomerate.conglomerate_id === brand.conglomerate_id,
        );
        return conglomerate;
      }
    });
    return conglomerate;
  },
  /**
   * id = 0b8b1bb9-af80-4753-9a6f-2d109d5107cb
   * idAlias = 'segmentation_id'
   * resource = 'segmentations'
   * valueToFetch = 'name'
   *
   */
  getResourceValue: (state) => (id, idAlias, resource, valueToFetch) => {
    const item = state.cruds[resource].find((item) => item[idAlias] === id);
    return item && item.hasOwnProperty(valueToFetch) ? item[valueToFetch] : '';
  },
  getResourceValueFirestore: (state) => (id, resource, valueToFetch) => {
    const item = state.cmsResources[resource].find((item) => item.id === id);
    return item && item.hasOwnProperty(valueToFetch) ? item[valueToFetch] : '';
  },
  allParentSegmentations: (state) => state.cruds.segmentations.filter(
    (seg) => !seg.hasOwnProperty('segmentation_parent'),
  ),
};

export default {
  state,
  mutations,
  actions,
  getters,
};

function storeJSON(data, name) {
  const stringJSON = JSON.stringify(data);
  localStorage.setItem(name, stringJSON);
}

function readJSON(name) {
  const parsedData = JSON.parse(localStorage.getItem(name));
  return parsedData || [];
}

function setThemeRecords(themes) {
  themes.forEach((theme) => {
    Vue.set(theme, 'product_leaders', []);
    getProductLeaders(theme.theme_id, theme);
  });
}

function getProductLeaders(themeId, themeObj) {
  const productLeaders = firestoreDB
    .collectionGroup(`${process.env.ENV_SHORTNAME}_productLeaders`)
    .where('themeId', '==', themeId);
  productLeaders.get().then((querySnapshot) => {
    querySnapshot.forEach((doc) => {
      themeObj.product_leaders.push(doc.id);
    });
  });
}



// WEBPACK FOOTER //
// ./src/store/modules/crudData/index.js